import React, { useState, useEffect, useCallback } from 'react';
import RadarChart, { AXES, isFilledVal } from './RadarChart';
import ChartGuide from './ChartGuide';
import { PAIR_GROUPS, MIN_FILLED_PER_SIDE, evaluateHarmony, withoutStaleAutomaticResult } from './pairingHarmony';
export { PAIR_GROUPS, MIN_FILLED_PER_SIDE, evaluateHarmony };
import WineAutocomplete from './WineAutocomplete';
import DishAutocomplete from './DishAutocomplete';
import AttachmentPicker from './AttachmentPicker';
import LabelScanner from './LabelScanner';
import { applyScanToWineForm, uploadWineLabel } from './labelScan';
import { useI18n } from './i18n';
import { exportPdf } from './pdfExport';
import { authFetch } from './auth';
import { getCurrentUserId } from './attachments';
import { ComparisonTable, FOOD_COMPARISON_SPEC } from './WineCatalog';
import WineSheetPicker from './WineSheetPicker';
import FoodSheetPicker from './FoodSheetPicker';
import { PAIRING_OUTCOMES, matchKey } from './codes';
import { completeValues, replaceValues, emptyParams } from './onavToPairing';
import { completeFoodValues, replaceFoodValues, emptyFoodKeys } from './foodSheetImport';

const DRAFT_KEY = 'tnh2_draft_pairing';

const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:8100/api' : '/api';

const INITIAL = {
  dish_name: '',
  wine_name: '',
  vintage: '',
  producer: '',
  tasting_date: new Date().toISOString().slice(0, 10),
  taster: '',
  venue: '',
  chef: '',
  // Food parameters (0-10); null = not filled in (distinct from 0): the slider starts empty
  food_sweetness: null, food_sweet_tendency: null, food_fatness: null,
  food_unctuousness: null, food_succulence: null, food_sapidity: null,
  food_bitter_tendency: null, food_acid_tendency: null,
  food_aromaticity: null, food_spiciness: null, food_persistence: null, food_structure: null,
  // Wine parameters (0-10); null = not filled in
  wine_sweetness: null, wine_acidity: null, wine_sapidity: null, wine_effervescence: null,
  wine_tannicity: null, wine_alcohol: null, wine_softness: null,
  wine_olfactory_intensity: null, wine_body: null, wine_pai: null,
  // Evaluation
  pairing_outcome: '',
  score: '',
  notes: '',
  visibility: 'private',
  food_sheet_id: null,
  // The pairing keeps its own Mercadini wine sheet ("wine profile"): the recalled one (left unchanged) and the
  // ONAV / ASPI sheets the values were imported from (traceability only)
  wine_profile_id: null,
  wine_source_sheet_ids: [],
};

// Keys that identify the wine: changing them detaches the recalled wine profile (it belongs to one wine)
const WINE_IDENTITY = ['wine_name', 'vintage', 'producer'];
const wineIdentityChanged = (prev, next) => WINE_IDENTITY.some((key) => String(prev[key] ?? '') !== String(next[key] ?? ''));
// Applies `next` over `prev` and drops the wine profile and its source sheets when the wine itself changed
const withWineDetached = (prev, next) => (wineIdentityChanged(prev, next) ? { ...next, wine_profile_id: null, wine_source_sheet_ids: [] } : next);

// Mercadini slider 0-10. `value` null/undefined/'' = NOT FILLED IN (shown as "—", distinct from 0): the value is only stored
// once the user touches the slider (a click on the thumb at 0 counts), and the clear button brings it back to not filled in.
export function RangeSlider({ name, value, onChange, side }) {
  const { t } = useI18n();
  const [showInfo, setShowInfo] = useState(false);
  const label = t(`pairing.p.${name}`);
  const defKey = `pairing.def.${name}`;
  const definition = t(defKey) !== defKey ? t(defKey) : null;
  const filled = isFilledVal(value);
  return (
    <div className={`pairing-slider${filled ? '' : ' unfilled'}`}>
      <label>
        <span className={`pairing-label ${side}`}>{label}</span>
        <span className="pairing-value" title={filled ? undefined : t('pairing.form.not_filled')}>{filled ? value : '—'}</span>
        {definition && (
          <button type="button" className="info-btn"
            onClick={(e) => { e.preventDefault(); setShowInfo(!showInfo); }}
            title={t('pairing.form.how_to')}>?</button>
        )}
        {filled && (
          <button type="button" className="chip score-clear" onClick={() => onChange(name, null)}
            title={t('pairing.form.clear_hint')}>{t('pairing.form.clear')}</button>
        )}
      </label>
      <input type="range" min={0} max={10} value={filled ? Number(value) : 0}
        className={`range-${side}${filled ? '' : ' unfilled'}`}
        aria-label={filled ? label : `${label}: ${t('pairing.form.not_filled')}`}
        onChange={(e) => onChange(name, parseInt(e.target.value, 10))}
        onClick={(e) => { if (!filled) onChange(name, parseInt(e.target.value, 10)); }} />
      {showInfo && definition && <div className="info-box">{definition}</div>}
    </div>
  );
}

// Keeps only the known fields of a stored draft / API object, over the defaults
function pickKnown(source) {
  const out = { ...INITIAL };
  Object.keys(INITIAL).forEach((key) => {
    if (source[key] !== undefined && source[key] !== null) out[key] = source[key];
  });
  return out;
}

function loadDraft() {
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) return pickKnown(JSON.parse(saved));
  } catch { /* ignore */ }
  return INITIAL;
}

// Renders in bold the segments of a translated text enclosed in ** **
function rich(text) {
  return text.split('**').map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));
}

const emptyToNull = (value) => (typeof value === 'string' && value.trim() === '' ? null : value);

function PairingForm({ onSubmit, initialData, onCancel }) {
  const { t, tc, formatDate } = useI18n();
  const editing = !!initialData;
  // An old pairing reopened for editing: the score/outcome stored automatically by the previous calculation are dropped (saving recalculates them)
  const [form, setForm] = useState(() => (initialData ? withoutStaleAutomaticResult(pickKnown(initialData)) : loadDraft()));
  const [importNote, setImportNote] = useState(null);
  const [foodSheets, setFoodSheets] = useState([]);
  const [foodNote, setFoodNote] = useState(null);
  const [wineProfiles, setWineProfiles] = useState([]);
  const [me, setMe] = useState(null);
  const [attachmentItems, setAttachmentItems] = useState([]);
  const [labelPhoto, setLabelPhoto] = useState(null); // scanned label photo, uploaded to the wine catalog after saving

  const loadFoodSheets = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/food-sheets?limit=200`);
      if (res && res.ok) setFoodSheets(await res.json());
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadFoodSheets(); }, [loadFoodSheets]);

  // Mercadini wine profiles (the pairing's own wine sheets): reloaded after every save so that the new one can be recalled
  const loadWineProfiles = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/wine-profiles?limit=200`);
      if (res && res.ok) setWineProfiles(await res.json());
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadWineProfiles(); }, [loadWineProfiles]);
  useEffect(() => { getCurrentUserId().then((id) => setMe(id || null)).catch(() => setMe(null)); }, []);

  // Food sheets of the dish in the form, for the comparison with the entered values
  const sameDishSheets = (() => {
    const key = matchKey(form.dish_name);
    return key ? foodSheets.filter((sheet) => matchKey(sheet.dish_name) === key) : [];
  })();

  // Recall from the food-sheet picker: always from ONE sheet (no averaging between sheets).
  // * 'replace' (Import): the dish, the 12 values (a parameter the sheet did not fill in stays not filled in), venue, chef and the
  //   link to that food sheet;
  // * 'complete': only the parameters that are still empty are filled, nothing is overwritten; the link to the food sheet is
  //   dropped (the values are no longer exactly those of the sheet): saving creates a new food sheet, as for hand-edited values.
  const recallFood = ({ mode, sheet }) => {
    if (mode === 'complete') {
      const { values, filled } = completeFoodValues(form, sheet);
      setForm((prev) => ({ ...prev, ...values, food_sheet_id: null }));
      setFoodNote({ type: 'ok', text: filled.length
        ? t('pairing.foodpicker.completed', { count: filled.length, dish: sheet.dish_name })
        : t('pairing.foodpicker.nothing_to_complete') });
      return;
    }
    const values = replaceFoodValues(sheet);
    setForm((prev) => ({
      ...prev, dish_name: sheet.dish_name, ...values, food_sheet_id: sheet.id,
      venue: sheet.venue || prev.venue, chef: sheet.chef || prev.chef,
    }));
    const left = emptyFoodKeys(values).length;
    setFoodNote({ type: 'ok', text: t('pairing.food.loaded') + (left ? ` ${t('pairing.foodpicker.empty_left', { count: left })}` : '') });
  };

  // Recalls a wine from ONE ONAV / ASPI sheet or Mercadini wine profile (no averaging). The pairing always saves its own wine
  // sheet: `wine_source_sheet_ids` remembers the sheets the values come from, and a profile recalled on its own is linked
  // (`wine_profile_id`) as long as nothing changes it.
  // * 'replace' (Import): the ten values become those of the sheet (what the sheet does not give becomes not filled in);
  // * 'complete': only the empty parameters are filled; the link to a recalled profile is dropped (that profile becomes one of the
  //   sources together with the sheet used to complete).
  const importFromSheets = ({ mode, wine, sheet, values, direct, estimated }) => {
    if (mode === 'complete') {
      const { values: full, filled } = completeValues(form, values);
      setForm((prev) => ({
        ...prev, ...full, wine_profile_id: null,
        wine_source_sheet_ids: [...new Set([...(prev.wine_source_sheet_ids || []), ...(prev.wine_profile_id ? [prev.wine_profile_id] : []), sheet.id])].slice(0, 20),
      }));
      setImportNote({ mode, kind: sheet.type, direct: filled.filter((f) => !estimated.includes(f)), estimated: filled.filter((f) => estimated.includes(f)), empty: emptyParams(full), completed: filled.length });
      return;
    }
    const full = replaceValues(values);
    setForm((prev) => ({
      ...prev,
      wine_name: wine.name || prev.wine_name,
      vintage: wine.vintage || '',
      producer: wine.producer || '',
      ...full,
      wine_profile_id: sheet.type === 'profile' ? sheet.id : null,
      wine_source_sheet_ids: sheet.type === 'profile' ? [] : [sheet.id],
    }));
    setImportNote({ mode, kind: sheet.type, direct, estimated, empty: emptyParams(full), completed: 0 });
  };

  // Save the draft on every change
  useEffect(() => { if (!editing) localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }, [form, editing]);

  const handleChange = useCallback((name, value) => {
    // Editing a food parameter by hand detaches the recalled food sheet; editing a wine parameter (or the wine itself)
    // detaches the recalled wine profile, and changing the wine also forgets the source sheets of the imported values
    setForm((prev) => withWineDetached(prev, {
      ...prev, [name]: value,
      ...(name.startsWith('food_') ? { food_sheet_id: null } : {}),
      ...(name.startsWith('wine_') ? { wine_profile_id: null } : {}),
    }));
  }, []);

  const handleInputChange = (e) => {
    handleChange(e.target.name, e.target.value);
  };

  const handleReset = () => {
    if (!window.confirm(t('pairing.form.confirm_reset'))) return;
    setForm(INITIAL);
    setAttachmentItems([]);
    setLabelPhoto(null);
    localStorage.removeItem(DRAFT_KEY);
  };

  // Sheet data in the API format (used both to save and to export the PDF)
  const buildData = () => {
    if (!form.dish_name.trim() || !form.wine_name.trim()) {
      alert(t('pairing.form.alert_required'));
      return null;
    }
    const harmony = evaluateHarmony(form);
    return {
      ...form,
      wine_profile_id: form.wine_profile_id || null,
      wine_source_sheet_ids: Array.isArray(form.wine_source_sheet_ids) ? form.wine_source_sheet_ids : [],
      vintage: form.vintage ? parseInt(form.vintage, 10) : null,
      producer: emptyToNull(form.producer),
      taster: emptyToNull(form.taster),
      venue: emptyToNull(form.venue),
      chef: emptyToNull(form.chef),
      notes: emptyToNull(form.notes),
      tasting_date: emptyToNull(form.tasting_date),
      // without enough filled-in sensations there is no computed score nor outcome (null, never a made-up value)
      score: form.score ? parseFloat(form.score) : (harmony.enough ? Math.round(harmony.score * 10) / 10 : null),
      pairing_outcome: form.pairing_outcome || harmony.code || null,
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = buildData();
    if (!data) return;
    // After saving, the food sheet has been created automatically: reload the list so it can be recalled
    const photo = editing ? null : labelPhoto;
    Promise.resolve(onSubmit(data, editing ? [] : attachmentItems))
      .then(() => uploadWineLabel({ name: data.wine_name, vintage: data.vintage, producer: data.producer }, photo))
      .then(() => Promise.all([loadFoodSheets(), loadWineProfiles()]));
    if (editing) return;
    setForm(INITIAL);
    setAttachmentItems([]);
    setLabelPhoto(null);
    localStorage.removeItem(DRAFT_KEY);
  };

  // PDF with the current values of the form, AIS graphic sheet included
  const handleExportPdf = () => {
    const data = buildData();
    if (data) exportPdf({ path: 'pairings', data, chartSelector: '.mercadini-svg', errorMessage: t('ui.export_error') });
  };

  const harmony = evaluateHarmony(form);

  const foodAxes = AXES.filter((axis) => axis.side === 'food');
  const wineAxes = AXES.filter((axis) => axis.side === 'wine');

  return (
    <form className="sheet-form" onSubmit={handleSubmit}>
      {/* ── Choose wine and dish: sheets to recall, side by side (otherwise the values are entered by hand below) ── */}
      <fieldset className="pairing-pick">
        <legend>{t('pairing.pick.legend')}</legend>
        <div className="pairing-pick-grid">
          <div className="pairing-pick-panel wine">
            <h4 className="pairing-pick-title">{t('pairing.pick.wine_title')}</h4>
            <small className="import-hint">{t('pairing.pick.wine_hint')}</small>
            <WineSheetPicker onImport={importFromSheets} profiles={wineProfiles}
              currentWine={{ name: form.wine_name, vintage: form.vintage, producer: form.producer }} />
            {importNote && (
              <div className="import-note">
                <div>
                  {importNote.mode === 'complete'
                    ? (importNote.completed
                      ? t('pairing.import.completed', { count: importNote.completed, kind: t(`pairing.picker.kind.${importNote.kind}`) })
                      : t('pairing.import.nothing_to_complete'))
                    : t('pairing.import.done_one', { kind: t(`pairing.picker.kind.${importNote.kind}`) })}
                </div>
                {importNote.estimated.length > 0 && (
                  <div>{t('pairing.import.estimated', { params: importNote.estimated.map((k) => t(`pairing.p.${k}`)).join(', ') })}</div>
                )}
                {importNote.empty.length > 0 && (
                  <div>{t('pairing.import.manual', { params: importNote.empty.map((k) => t(`pairing.p.${k}`)).join(', ') })}</div>
                )}
              </div>
            )}
            {/* The pairing saves its own wine sheet; for a recalled one, which sheet it is linked to */}
            <div className="import-note wine-profile-note">
              <div>{t('pairing.wineprofile.note')}</div>
              {form.wine_profile_id && (() => {
                const linked = wineProfiles.find((x) => x.id === form.wine_profile_id);
                if (!linked) return <div className="wine-profile-linked">{t('pairing.wineprofile.linked_unknown')}</div>;
                const who = !linked.owner_id ? t('pairing.picker.anonymous') : (me && linked.owner_id === me ? t('pairing.picker.you') : (linked.taster || t('pairing.picker.other')));
                return (
                  <div className="wine-profile-linked">
                    {t('pairing.wineprofile.linked', { date: linked.tasting_date ? formatDate(linked.tasting_date) : '—', who })}
                  </div>
                );
              })()}
              {!form.wine_profile_id && form.wine_source_sheet_ids.length > 0 && (
                <div className="wine-profile-linked">{t('pairing.wineprofile.sources', { count: form.wine_source_sheet_ids.length })}</div>
              )}
            </div>
          </div>
          <div className="pairing-pick-panel food">
            <h4 className="pairing-pick-title">{t('pairing.pick.food_title')}</h4>
            <small className="import-hint">{t('pairing.pick.food_hint')}</small>
            <FoodSheetPicker sheets={foodSheets} dishName={form.dish_name} onImport={recallFood} />
            {foodNote && <div className="import-note" style={{ color: foodNote.type === 'err' ? '#a33' : undefined }}>{foodNote.text}</div>}
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend>{t('pairing.form.legend_info')}</legend>
        <LabelScanner kind="wine" onPhoto={setLabelPhoto}
          onApply={(values, scan, options) => setForm((prev) => withWineDetached(prev, applyScanToWineForm(prev, values, options)))} />
        <div className="form-grid">
          <div className="form-field">
            <label>{t('pairing.p.dish_name')} *</label>
            <DishAutocomplete
              value={form.dish_name}
              onChange={(v) => handleChange('dish_name', v)}
              onSelect={(dish) => handleChange('dish_name', dish.name)}
              required
            />
          </div>
          <div className="form-field">
            <label>{t('pairing.p.wine_name')} *</label>
            <WineAutocomplete
              value={form.wine_name}
              onChange={(v) => handleChange('wine_name', v)}
              onSelect={(wine) => setForm((prev) => withWineDetached(prev, {
                ...prev,
                wine_name: wine.name,
                vintage: wine.vintage || '',
                producer: wine.producer || '',
              }))}
              required
            />
          </div>
          <div className="form-field">
            <label>{t('pairing.p.producer')}</label>
            <input name="producer" value={form.producer || ''} onChange={handleInputChange} />
          </div>
          <div className="form-field">
            <label>{t('pairing.p.vintage')}</label>
            <input name="vintage" type="number" min={1900} max={2030} value={form.vintage} onChange={handleInputChange} />
          </div>
          <div className="form-field">
            <label>{t('pairing.p.tasting_date')}</label>
            <input name="tasting_date" type="date" value={form.tasting_date || ''} onChange={handleInputChange} />
          </div>
          <div className="form-field">
            <label>{t('pairing.p.taster')}</label>
            <input name="taster" value={form.taster || ''} onChange={handleInputChange} />
          </div>
          <div className="form-field">
            <label>{t('pairing.p.venue')}</label>
            <input name="venue" value={form.venue || ''} onChange={handleInputChange} />
          </div>
          <div className="form-field">
            <label>{t('pairing.p.chef')}</label>
            <input name="chef" value={form.chef || ''} onChange={handleInputChange} />
          </div>
        </div>
      </fieldset>

      {/* ── Live radar chart ── */}
      <fieldset>
        <legend>{t('pairing.form.legend_chart')}</legend>
        <RadarChart data={form} />
        <ChartGuide groups={PAIR_GROUPS} />
        <div className={`pairing-badge ${harmony.code.replace(/_/g, '-')}`}>
          {harmony.enough
            ? <>{tc('outcome', harmony.code)} {harmony.score > 0 ? `(${Math.round(harmony.score * 10) / 10}/10)` : ''}</>
            : (
              <span className="pairing-not-enough">
                {t('pairing.form.not_enough', { n: harmony.missingFood.length + harmony.missingWine.length })}
                {harmony.missingFood.length > 0 && <><br />{t('pairing.form.missing_food')}: {harmony.missingFood.map((key) => t(`pairing.p.${key}`)).join(', ')}</>}
                {harmony.missingWine.length > 0 && <><br />{t('pairing.form.missing_wine')}: {harmony.missingWine.map((key) => t(`pairing.p.${key}`)).join(', ')}</>}
              </span>
            )}
        </div>
      </fieldset>

      {/* ── FOOD parameters ── */}
      <fieldset>
        <legend className="legend-food">{t('pairing.form.legend_food')}</legend>
        {sameDishSheets.length > 0 && (
          <ComparisonTable ns="pairing" spec={FOOD_COMPARISON_SPEC}
            summary={t('food.compare_summary', { n: sameDishSheets.length })}
            rows={[...sameDishSheets, { ...form, id: 'current', columnLabel: t('food.current_col'), excludeFromStats: true }]} />
        )}

        <h4 className="section-subtitle">{t('pairing.form.sub_soft')}</h4>
        {foodAxes.filter((_, i) => i < 5).map((axis) => (
          <RangeSlider key={axis.key} name={axis.key}
            value={form[axis.key]} onChange={handleChange} side="food" />
        ))}
        <h4 className="section-subtitle">{t('pairing.form.sub_hard')}</h4>
        {foodAxes.filter((_, i) => i >= 5 && i < 8).map((axis) => (
          <RangeSlider key={axis.key} name={axis.key}
            value={form[axis.key]} onChange={handleChange} side="food" />
        ))}
        <h4 className="section-subtitle">{t('pairing.form.sub_aroma')}</h4>
        {foodAxes.filter((_, i) => i >= 8).map((axis) => (
          <RangeSlider key={axis.key} name={axis.key}
            value={form[axis.key]} onChange={handleChange} side="food" />
        ))}
      </fieldset>

      {/* ── WINE parameters ── */}
      <fieldset>
        <legend className="legend-wine">{t('pairing.form.legend_wine')}</legend>
        <h4 className="section-subtitle">{t('pairing.form.sub_wine_hard')}</h4>
        {wineAxes.filter((_, i) => i < 7).map((axis) => (
          <RangeSlider key={axis.key} name={axis.key}
            value={form[axis.key]} onChange={handleChange} side="wine" />
        ))}
        <h4 className="section-subtitle">{t('pairing.form.sub_concord')}</h4>
        {wineAxes.filter((_, i) => i >= 7).map((axis) => (
          <RangeSlider key={axis.key} name={axis.key}
            value={form[axis.key]} onChange={handleChange} side="wine" />
        ))}
      </fieldset>

      {/* ── Reference pairs, grouped ── */}
      <fieldset>
        <legend>{t('pairing.form.legend_pairs')}</legend>
        {PAIR_GROUPS.map((group, gi) => {
          // Group summary: average of the deltas of the pairs where both sides are filled in (the others are left out)
          const diffs = group.pairs
            .filter((p) => isFilledVal(form[p.food]) && isFilledVal(form[p.wine]))
            .map((p) => Math.abs(Number(form[p.food]) - Number(form[p.wine])));
          const hasDiffs = diffs.length > 0;
          const avgDiff = hasDiffs ? diffs.reduce((a, b) => a + b, 0) / diffs.length : 0;
          const groupOk = !hasDiffs || avgDiff <= 2;

          return (
            <div key={gi} className="pair-group">
              <div className={`pair-group-header ${group.kind} ${hasDiffs ? (groupOk ? 'ok' : 'warn') : 'unfilled'}`}>
                <span className="pair-group-name">
                  {t(group.name)}
                </span>
                <span className={`pair-group-summary ${hasDiffs ? (groupOk ? 'ok' : 'warn') : 'unfilled'}`}>
                  {hasDiffs ? `${groupOk ? '●' : '○'} ${t('pairing.form.delta_avg', { v: avgDiff.toFixed(1) })}` : '—'}
                </span>
              </div>
              <div className="pairs-grid">
                {group.pairs.map((p, i) => {
                  const foodLabel = t(`pairing.p.${p.food}`);
                  const wineLabel = t(`pairing.p.${p.wine}`);
                  const complete = isFilledVal(form[p.food]) && isFilledVal(form[p.wine]);
                  const diff = complete ? Math.abs(Number(form[p.food]) - Number(form[p.wine])) : null;
                  const ok = complete && diff <= 2;
                  const show = (v) => (isFilledVal(v) ? v : '—');
                  return (
                    <div key={i} className={`pair ${group.kind} ${complete ? (ok ? 'ok' : 'warn') : 'unfilled'}`}>
                      <span className="pair-food">{foodLabel} ({show(form[p.food])})</span>
                      <span className="pair-kind">{group.kind === 'concordance' ? '=' : '↔'}</span>
                      <span className="pair-wine">{wineLabel} ({show(form[p.wine])})</span>
                      <span className={`pair-status ${complete ? (ok ? 'ok' : 'warn') : 'unfilled'}`}>
                        {complete ? `${ok ? '●' : '○'} Δ${diff}` : '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* How the harmony is computed */}
        <div className="harmony-explanation">
          <h4 className="section-subtitle">{t('pairing.form.calc_title')}</h4>
          <p className="calc-detail">{rich(t('pairing.form.calc_p1'))}</p>
          <p className="calc-detail">
            {rich(t('pairing.form.calc_p2', { n: harmony.pairs, v: harmony.enough ? harmony.avgDiff.toFixed(1) : '—' }))}
          </p>
          <p className="calc-detail">
            {rich(t('pairing.form.calc_p3', { v: harmony.enough ? harmony.score.toFixed(1) : '—' }))}
          </p>
          <p className="calc-detail">{t('pairing.form.calc_cap')}{harmony.capped ? ` ${t('pairing.form.calc_capped_now')}` : ''}</p>
          <p className="calc-detail">
            {t('pairing.form.calc_p4', {
              a: tc('outcome', 'harmonious'), b: tc('outcome', 'fairly_harmonious'),
              c: tc('outcome', 'slightly_harmonious'), d: tc('outcome', 'disharmonious'),
            })}
          </p>
          <p className="calc-detail">{t('pairing.form.calc_p5')}</p>
        </div>
      </fieldset>

      {/* ── Final evaluation ── */}
      <fieldset>
        <legend>{t('pairing.form.legend_final')}</legend>
        <div className="form-field">
          <label>{t('pairing.p.pairing_outcome')}</label>
          <div className="chip-group">
            {PAIRING_OUTCOMES.map((outcome) => (
              <button type="button" key={outcome}
                className={`chip ${(form.pairing_outcome || harmony.code) === outcome ? 'selected' : ''}`}
                onClick={() => handleChange('pairing_outcome', outcome)}>
                {tc('outcome', outcome)}
              </button>
            ))}
          </div>
        </div>
        <div className="form-field">
          <label>{t('pairing.p.score')}</label>
          <input name="score" type="number" min={0} max={10} step={0.5}
            value={form.score} onChange={handleInputChange}
            placeholder={harmony.enough ? t('pairing.form.suggested', { v: Math.round(harmony.score * 10) / 10 }) : '—'} />
        </div>
        <div className="form-field">
          <label>{t('pairing.p.notes')}</label>
          <textarea name="notes" value={form.notes || ''} onChange={handleInputChange} rows={3}
            placeholder={t('pairing.form.note_placeholder')} />
        </div>
      </fieldset>

      {!editing && (
        <fieldset>
          <legend>{t('attachments.title')}</legend>
          <AttachmentPicker items={attachmentItems} onChange={setAttachmentItems} />
        </fieldset>
      )}

      <div className="form-field publish-opt">
        <label className="publish-label">
          <input type="checkbox" checked={form.visibility === 'public'}
            onChange={(e) => handleChange('visibility', e.target.checked ? 'public' : 'private')} />
          {' '}{t('ui.publish_label')}
        </label>
        <small className="import-hint">{t('ui.publish_hint')}</small>
      </div>

      <div className="form-buttons">
        <button type="submit" className="submit-btn">{editing ? t('pairing.form.btn_save_edit') : t('pairing.form.btn_save')}</button>
        <button type="button" className="reset-btn" onClick={handleExportPdf}>{t('ui.export_pdf')}</button>
        {editing
          ? <button type="button" className="reset-btn" onClick={onCancel}>{t('ui.cancel')}</button>
          : <button type="button" className="reset-btn" onClick={handleReset}>{t('ui.reset')}</button>}
      </div>
    </form>
  );
}

export default PairingForm;
